<template>
    <ies-form-field :field="field">
        <v-autocomplete
            v-model="input"
            :items="policiesWithNew"
            :required="this.field.required"
            :disabled="this.field.disabled"
            outlined
            dense
            @change="onPolicyChange"
        ></v-autocomplete>

        <v-row v-if="showFreeCancellationDays" no-gutters align="center" class="mt-2">
            <v-col cols="6" sm="3" md="2" lg="1" class="pr-2">
                <v-text-field
                    v-model="freeCancellationDays"
                    type="number"
                    min="1"
                    outlined
                    dense
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col cols="auto" class="body-2">days before arrival</v-col>
        </v-row>

        <v-card v-if="showAddCustomPolicy" class="mt-2 mb-2 pa-3">
            <v-card-text>
                <v-row class="font-weight-bold caption mb-1" no-gutters>
                    <v-col cols="3">Amount</v-col>
                    <v-col cols="4">Days before arrival</v-col>
                    <v-col cols="4">Transferrable</v-col>
                    <v-col cols="1"></v-col>
                </v-row>

                <v-row v-for="(scheduleRow, index) in scheduleRows" :key="index" no-gutters align="center" class="mb-2">
                    <v-col cols="3" class="pr-2">
                        <v-text-field
                            v-model="scheduleRow.amount"
                            :placeholder="index === 0 ? 'e.g. 35% of total' : (index === scheduleRows.length - 1 ? 'Full amount' : '')"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="4" class="pr-2">
                        <v-text-field
                            v-model="scheduleRow.days"
                            type="number"
                            min="1"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="4" class="pr-2">
                        <v-checkbox v-model="scheduleRow.future" dense hide-details class="mt-0 pt-0"></v-checkbox>
                    </v-col>
                    <v-col cols="1" class="text-center">
                        <v-btn icon small @click="removeScheduleRow(index)" :disabled="scheduleRows.length === 1">
                            <v-icon small>close</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>

                <v-row no-gutters align="center" class="mb-2">
                    <v-col cols="3" class="pr-2">
                        <v-text-field
                            v-model="noShowRow.amount"
                            outlined
                            dense
                            hide-details
                        ></v-text-field>
                    </v-col>
                    <v-col cols="4" class="pr-2 body-2 grey--text text--darken-1">
                        No show or early departure
                    </v-col>
                    <v-col cols="4" class="pr-2">
                        <v-checkbox v-model="noShowRow.future" label="Transferrable" dense hide-details class="mt-0 pt-0"></v-checkbox>
                    </v-col>
                    <v-col cols="1"></v-col>
                </v-row>

                <v-btn small text @click="addScheduleRow" :disabled="scheduleRows.length >= 3">+ Add row</v-btn>
            </v-card-text>
        </v-card>

        <v-btn v-if="allowTextEdit" icon small @click="editText = !editText" :title="editText ? 'Lock policy text' : 'Manually edit policy text'">
            <v-icon small>{{ editText ? 'lock_open' : 'lock' }}</v-icon>
        </v-btn>
        <v-textarea
            v-model="text"
            :required="this.field.required"
            :disabled="this.field.disabled || !editText || !allowTextEdit"
        >
        </v-textarea>
    </ies-form-field>
</template>

<script>
export default {
    name: 'ies-input-rate-type-cancellation-policy-key',
    props: ['field', 'page'],
    components: {
        'ies-form-field': require('./FormField.vue').default,
    },
    data () {
        return {
            input: parseInt(this.field.value || this.field.oldValue) || null,
            policies: [],
            hotelKey: null,
            rateTypeKey: null,
            hotelName: null,
            editText: false,
            text: null,
            showFreeCancellationDays: false,
            freeCancellationDays: null,
            showAddCustomPolicy: false,
            scheduleRows: [
                { amount: null, days: null, future: false },
                { amount: null, days: null, future: false }
            ],
            noShowRow: { amount: null, future: false },
        }
    },
    computed: {
        submissionData: function() {
            let policyMode = this.showAddCustomPolicy ? 3 : this.getModeForInput(this.input);
            return {
                policy_mode: policyMode,
                policy_text: this.text,
                free_cancellation_cutoff_days: this.freeCancellationDays,
                rateTypeKey: this.rateTypeKey
            };
        },

        allowTextEdit: function() {
            if (this.showAddCustomPolicy) {
                return true;
            }
            let mode = this.getModeForInput(this.input);
            return mode === 3;
        },

        policiesWithNew: function() {
            return this.policies.concat([{ text: 'New Policy...', value: '__new__' }]);
        },

        generatedText: function() {
            let result = '';
            let hasAnyFuture = false;
            let hotelName = this.hotelName || 'the property';

            this.scheduleRows.forEach(function(scheduleRow) {
                if (!scheduleRow.amount || !scheduleRow.days) {
                    return;
                }
                result += 'For cancellations less than ' + scheduleRow.days + ' days before arrival, ' + hotelName + ' will retain ' + scheduleRow.amount + '. ';
                if (scheduleRow.future) {
                    hasAnyFuture = true;
                }
            });

            if (this.noShowRow.amount) {
                result += 'For no-shows or early departures, ' + hotelName + ' will retain ' + this.noShowRow.amount + '. ';
                if (this.noShowRow.future) {
                    hasAnyFuture = true;
                }
            }

            if (hasAnyFuture) {
                result += 'Cancellation fees may be used for a future stay.';
            }

            return result.trimEnd();
        }
    },
    watch: {
        input: function(value) {
            let selectedMode = this.getModeForInput(value);
            this.showFreeCancellationDays = (selectedMode === 2);
            this.updateTextFromPolicy(value);
        },

        submissionData: {
            deep: true,
            handler: function(value) {
                this.field.value = value;
            }
        },

        freeCancellationDays: function() {
            if (this.showFreeCancellationDays && !this.editText && this.freeCancellationDays) {
                this.text = 'Free cancellation up to ' + this.freeCancellationDays + ' days before arrival.';
            }
        },

        scheduleRows: {
            deep: true,
            handler: function() {
                if (this.showAddCustomPolicy && !this.editText) {
                    this.text = this.generatedText;
                }
            }
        },

        noShowRow: {
            deep: true,
            handler: function() {
                if (this.showAddCustomPolicy && !this.editText) {
                    this.text = this.generatedText;
                }
            }
        }
    },

    methods: {
        setRateTypeKeyFromUrl: function() {
            if (!this.page || !this.page.edit_url) {
                return;
            }

            let bits = this.page.edit_url.split('/');
            let foundIndex = null;

            bits.forEach(function(bit, index) {
                if (bit == '{RateType.rate_type_key}') {
                    foundIndex = index;
                }
            });

            if (foundIndex !== null) {
                let urlBits = location.pathname.split('/').splice(1);
                this.rateTypeKey = parseInt(urlBits[foundIndex]);
            }
        },

        setHotelKeyFromUrl: function() {
            if (!this.page || !this.page.edit_url) {
                return;
            }

            let bits = this.page.edit_url.split('/');
            let foundIndex = null;

            bits.forEach(function(bit, index) {
                if (bit == '{Hotel.hotel_key}' || bit == '{RateType.hotel_key}') {
                    foundIndex = index;
                }
            });

            if (foundIndex !== null) {
                let urlBits = location.pathname.split('/').splice(1);
                this.hotelKey = parseInt(urlBits[foundIndex]);
            }
        },

        onPolicyChange: function(value) {
            if (value === '__new__') {
                this.input = null;
                this.showAddCustomPolicy = true;
                this.showFreeCancellationDays = false;
                this.text = this.generatedText;
            } else {
                this.showAddCustomPolicy = false;
                let selectedMode = this.getModeForInput(value);
                this.showFreeCancellationDays = (selectedMode === 2);
            }
        },

        getModeForInput: function(inputValue) {
            let selectedPolicy = this.policies.find(function(policy) {
                return policy.value === inputValue;
            });

            return selectedPolicy ? selectedPolicy.mode : null;
        },

        updateTextFromPolicy: function(value) {
            if (!value || value === '__new__') {
                return;
            }

            let selectedMode = this.getModeForInput(value);
            if (selectedMode === 2) {
                return;
            }

            let selectedPolicy = this.policies.find(function(policy) {
                return policy.value === value;
            });

            if (selectedPolicy) {
                this.text = selectedPolicy.text;
            }
        },

        addScheduleRow: function() {
            this.scheduleRows.push({ amount: null, days: null, future: false });
        },

        removeScheduleRow: function(index) {
            this.scheduleRows.splice(index, 1);
        },

        getPolicies: function() {
            axios.get('/api/rate-type-cancellation-policies/' + this.hotelKey).then((response) => {
                this.hotelName = response.data.hotelName;

                this.policies = response.data.policies.map(function(policy) {
                    return {
                        text: policy.text,
                        value: policy.key !== null ? policy.key : ('mode_' + policy.mode),
                        mode: policy.mode,
                        freeCancellationCutoffDays: policy.freeCancellationCutoffDays || null,
                    };
                });

                this.updateTextFromPolicy(this.input);

                let selectedMode = this.getModeForInput(this.input);
                if (selectedMode === 2) {
                    this.showFreeCancellationDays = true;
                    let currentInput = this.input;
                    let selectedPolicy = this.policies.find(function(policy) {
                        return policy.value === currentInput;
                    });
                    if (selectedPolicy && selectedPolicy.freeCancellationCutoffDays) {
                        this.freeCancellationDays = selectedPolicy.freeCancellationCutoffDays;
                    }
                }
            });
        }
    },
    created: function() {
        this.setRateTypeKeyFromUrl();
        this.setHotelKeyFromUrl();
        this.getPolicies();
    }
}
</script>
